/**
 * Filter inputs shared by the Price Rules and Condition Discounts tabs.
 * Choices come from the facets / conditions resources. A facet is a tag +
 * value pair, so both live under a single
 * "Facet" filter: picking a tag scopes (and enables) the searchable value
 * dropdown to that tag's values, which keeps the value list usable even when
 * a partner has thousands of facet values across all tags.
 */
import { useEffect, useRef } from 'react';
import { AutocompleteInput, SelectInput, TextInput, useGetList, useListContext } from 'react-admin';
import { Box } from '@mui/material';

import type { FacetGroup } from '@src/facets';
import type { ConditionOption } from '@src/conditions';

import type { PricingType } from '../pricingRules/types';
import { useNotifyOnError } from './useNotifyOnError';

/**
 * Combined Facet filter: a searchable tag dropdown paired with a dependent,
 * searchable value dropdown scoped to the chosen tag. Selecting a tag without
 * a value (or vice versa) is meaningless, so they are added and removed as one
 * unit. The filter element's `source` (`tag`) and `label` (`Facet`) are read
 * by react-admin's FilterButton to key and label the single "Add filter" menu
 * item; `FacetFilterSync` handles clearing the value when the tag is removed.
 */
const FacetFilterInput = ({ source, label }: { source: string, label: string }) => {
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');
  const { filterValues, setFilters, displayedFilters } = useListContext();

  const selectedTag = filterValues?.tag as string | undefined;

  const tagChoices = facetGroups.map(fg => ({ id: fg.tag, name: fg.tag }));
  const tagFacet = facetGroups.find(fg => fg.tag === selectedTag);
  const nameChoices = tagFacet?.values.map(v => ({ id: v.name, name: v.name })) || [];

  return (
    <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'flex-start' }} data-filter-label={label}>
      <AutocompleteInput
        source={source}
        label="Facet tag"
        choices={tagChoices}
        helperText={false}
        // AutocompleteInput passes the selected id (not a DOM event); reset the
        // dependent value so it can't outlive a tag change.
        onChange={(value) => {
          setFilters(
            { ...filterValues, tag: (value as string) || undefined, name: undefined },
            displayedFilters,
          );
        }}
      />
      <AutocompleteInput
        source="name"
        label="Facet value"
        choices={nameChoices}
        disabled={!selectedTag}
        helperText={false}
        onChange={(value) => {
          setFilters({ ...filterValues, name: (value as string) || undefined }, displayedFilters);
        }}
      />
    </Box>
  );
};

/**
 * Keeps the dependent facet value consistent with the tag. Rendered as a direct
 * child of the List — so it lives independently of the removable filter element
 * — rather than inside that element, because removing the "Facet" filter
 * unmounts the element before it could clear the value, which would strand
 * `name` in the URL. When the tag is gone, clear the orphaned value so removing
 * the filter wipes both keys.
 */
export const FacetFilterSync = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const tag = filterValues?.tag as string | undefined;
  const name = filterValues?.name as string | undefined;

  // Refs so the effect reads current setters/values without re-running on every
  // render (filterValues/displayedFilters are new references each render).
  const filterValuesRef = useRef(filterValues);
  filterValuesRef.current = filterValues;
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;
  const displayedFiltersRef = useRef(displayedFilters);
  displayedFiltersRef.current = displayedFilters;

  useEffect(() => {
    if (!tag && name) {
      setFiltersRef.current({ ...filterValuesRef.current, name: undefined }, displayedFiltersRef.current);
    }
  }, [tag, name]);

  return null;
};

/** Filter elements for a rules list — pass as the List component's `filters` prop. */
export const useRuleFilters = (pricingType: PricingType): React.ReactElement[] => {
  const isCondition = pricingType === 'condition';

  const { data: facetGroups = [], error: facetsError } = useGetList<FacetGroup>('facets');
  const { data: conditions = [], error: conditionsError } =
    useGetList<ConditionOption>('conditions', {}, { enabled: isCondition });

  // Distinguish a failed load from "no options configured" — otherwise the
  // filter dropdowns just render empty with no explanation.
  useNotifyOnError(facetsError, "Couldn't load facet filter options — some filters may be unavailable.");
  useNotifyOnError(conditionsError, "Couldn't load condition filter options — some filters may be unavailable.");

  const choicesFor = (tag: string) =>
    facetGroups.find(fg => fg.tag === tag)?.values.map(v => ({ id: v.name, name: v.name })) || [];

  return [
    ...(isCondition ?
      [
        <SelectInput
          key="condition"
          source="condition"
          label="Condition"
          choices={conditions.map(c => ({ id: c.code, name: c.label }))}
          helperText={false}
          alwaysOn
        />,
      ] :
      []),
    <SelectInput
      key="department"
      source="department"
      label="Department"
      choices={choicesFor('department')}
      helperText={false}
      alwaysOn
    />,
    <SelectInput
      key="category"
      source="category"
      label="Category"
      choices={choicesFor('category')}
      helperText={false}
      alwaysOn
    />,
    <TextInput key="merchantCategory" source="merchantCategory" label="Merchant category" helperText={false} />,
    ...(isCondition ? [] : [
      <TextInput key="upc" source="upc" label="UPC" helperText={false} />,
    ]),
    <FacetFilterInput key="tag" source="tag" label="Facet" />,
  ];
};
